import { ref } from 'vue'
import {
  ALL_ACHIEVEMENTS,
  countUnlockedVisibleAchievements,
  type AchievementContext,
  type AchievementDef,
} from '~/data/achievements'

const VALID_ACHIEVEMENT_IDS = new Set(ALL_ACHIEVEMENTS.map((a) => a.id))

function countVisibleUnlocked(unlocked: Set<string>): number {
  return countUnlockedVisibleAchievements([...unlocked])
}
import { usePlayerStore } from '~/stores/usePlayerStore'
import { useInventoryStore, MAX_STARS, type OwnedPokemon } from '~/stores/useInventoryStore'
import { useCombatStore } from '~/stores/useCombatStore'
import { getPokemonTypes, getTypeEffectiveness } from '~/data/types'
import type { PokemonType } from '~/data/types'
import { battleTowerTrialOffenseTypeMult } from '~/data/battleTower'
import { REGION_STARTERS } from '~/data/starters'
import { getSlugGeneration } from '~/data/gacha'
import { MAIN_POKEDEX } from '~/data/pokedex'
import { EVOLUTIONS, getEvolutionStage, getEvolutionsFor } from '~/data/evolutions'
import { getPokemonPower, resolvePowerBaseSlug, toCombatDps } from '~/data/pokemonPower'
import type { Rarity } from '~/data/gacha'

const recentUnlocks = ref<AchievementDef[]>([])
let lastCheckTime = 0
const CHECK_INTERVAL_MS = 10_000 // check every 10 seconds

const FIRE_STARTER_SLUGS = new Set(REGION_STARTERS.map((r) => r.starters[1].slug))
const GRASS_STARTER_SLUGS = new Set(REGION_STARTERS.map((r) => r.starters[0].slug))
const WATER_STARTER_SLUGS = new Set(REGION_STARTERS.map((r) => r.starters[2].slug))

const ASH_FIRST_TEAM = new Set([
  'pikachu',
  'butterfree',
  'pidgeot',
  'bulbasaur',
  'charizard',
  'squirtle',
])

const ROCKET_ARSENAL_SLUGS = ['arbok', 'wobbuffet', 'meowth', 'weezing'] as const

/** Formes finales des starters de Kanto (Le Choix du Prof). */
const KANTO_FINAL_STARTERS = ['venusaur', 'charizard', 'blastoise'] as const
/** Trios / duos légendaires (présence en équipe). */
const KANTO_BIRDS = ['articuno', 'zapdos', 'moltres'] as const
const JOHTO_BEASTS = ['raikou', 'entei', 'suicune'] as const
const WEATHER_TRIO = ['groudon', 'kyogre', 'rayquaza'] as const
const CREATION_TRIO = ['dialga', 'palkia', 'giratina'] as const
const TAO_TRIO = ['reshiram', 'zekrom', 'kyurem'] as const
const EON_DUO = ['latios', 'latias'] as const

/** Les 4 Pokémon fétiches des « Le Choix de… » (Nashoba, Ashborn, Achlar, Guillian). */
const FOUR_ARCHITECTS_SLUGS = ['ceruledge', 'greninja', 'espeon', 'dubwool'] as const

/** Évolutions d'Évoli — dérivées des données d'évolution (future-proof). */
const EEVEELUTION_SLUGS = new Set(
  EVOLUTIONS.filter((e) => e.fromSlug === 'eevee').map((e) => e.toSlug),
)

const MEGA_FORM_RE = /-mega[xy]?$/
const REGIONAL_FORM_RE = /-(alola|galar|hisui|paldea)$/

/** Same effective DPS as useCombatLoop.getPokeDps (for achievement checks). */
function effectiveDpsForAchievement(
  poke: { slug: string; level: number; stars: number; isShiny: boolean; rarity?: Rarity },
  enemyTypes: PokemonType[] | undefined,
  combatGen: number,
  isEndgame: boolean,
  battleTowerZone: number | null,
): number {
  const pokeGen = getSlugGeneration(resolvePowerBaseSlug(poke.slug))
  const regionMult = isEndgame ? 1.0 : pokeGen >= combatGen ? 1.0 : 0.001
  const attackerTypes = getPokemonTypes(poke)
  let typeMult = 1
  if (enemyTypes && enemyTypes.length > 0) {
    typeMult = Math.max(...attackerTypes.map((atk) => getTypeEffectiveness(atk, enemyTypes)))
  }
  typeMult = battleTowerTrialOffenseTypeMult(typeMult, battleTowerZone)
  const permanentDps = Math.round(getPokemonPower(poke) * regionMult)
  return toCombatDps(permanentDps * typeMult)
}

/** Aucun type en double sur les 12 emplacements de types (6 Pokémon, max 2 types chacun). */
function teamTypesAllUnique(team: OwnedPokemon[]): boolean {
  if (team.length !== 6) return false
  const types: PokemonType[] = []
  for (const p of team) {
    types.push(...getPokemonTypes(p))
  }
  return types.length === new Set(types).size
}

/** 6 espèces distinctes, chacune une forme de base de starter (feu / plante / eau). */
function teamSixDistinctFromStarterSet(team: OwnedPokemon[], set: Set<string>): boolean {
  if (team.length !== 6) return false
  const slugs = team.map((p) => p.slug)
  if (new Set(slugs).size !== 6) return false
  return slugs.every((s) => set.has(s))
}

function teamAshFirst(team: OwnedPokemon[]): boolean {
  if (team.length !== 6) return false
  const got = new Set(team.map((p) => p.slug))
  if (got.size !== 6) return false
  for (const r of ASH_FIRST_TEAM) {
    if (!got.has(r)) return false
  }
  return got.size === ASH_FIRST_TEAM.size
}

function teamRocketArsenal(team: OwnedPokemon[]): boolean {
  if (team.length < ROCKET_ARSENAL_SLUGS.length) return false
  const got = new Set(team.map((p) => p.slug))
  return ROCKET_ARSENAL_SLUGS.every((s) => got.has(s))
}

/** Toutes les espèces du Pokédex principal possédées en version chromatique. */
function ownsFullShinyPokedex(collection: OwnedPokemon[]): boolean {
  const shinySlugs = new Set<string>()
  for (const p of collection) {
    if (p.isShiny) shinySlugs.add(p.slug)
  }
  if (shinySlugs.size < MAIN_POKEDEX.length) return false
  return MAIN_POKEDEX.every((entry) => shinySlugs.has(entry.slug))
}

/** L'équipe (peu importe sa taille) contient toutes les espèces listées. */
function teamHasAllSlugs(team: OwnedPokemon[], slugs: readonly string[]): boolean {
  if (team.length < slugs.length) return false
  const got = new Set(team.map((p) => p.slug))
  return slugs.every((s) => got.has(s))
}

/** 6 Pokémon en équipe, tous porteurs du type donné. */
function teamAllOfType(team: OwnedPokemon[], type: PokemonType): boolean {
  if (team.length !== 6) return false
  return team.every((p) => getPokemonTypes(p).includes(type))
}

/** 6 Pokémon en équipe partageant au moins un type commun à tous. */
function teamSharesCommonType(team: OwnedPokemon[]): boolean {
  if (team.length !== 6) return false
  let common: PokemonType[] = getPokemonTypes(team[0]!)
  for (let i = 1; i < team.length; i++) {
    const types = getPokemonTypes(team[i]!)
    common = common.filter((t) => types.includes(t))
    if (common.length === 0) return false
  }
  return common.length > 0
}

/** 6 Pokémon en équipe, tous en forme de base (aucune évolution amont). */
function teamAllBaseForms(team: OwnedPokemon[]): boolean {
  if (team.length !== 6) return false
  return team.every((p) => getEvolutionStage(p.slug) === 0)
}

/** 6 évolutions distinctes d'Évoli en équipe. */
function teamSixEeveelutions(team: OwnedPokemon[]): boolean {
  if (team.length !== 6) return false
  const slugs = team.map((p) => p.slug)
  if (new Set(slugs).size !== 6) return false
  return slugs.every((s) => EEVEELUTION_SLUGS.has(s))
}

/**
 * 6 Pokémon niveau 100 restés à leur forme de base ALORS qu'ils peuvent évoluer.
 * (forme de base = stade 0 ; capables d'évoluer = au moins une évolution en aval —
 * exclut les espèces sans évolution comme Tauros ou la plupart des légendaires.)
 */
function teamEvolutionRefused(team: OwnedPokemon[]): boolean {
  if (team.length !== 6) return false
  return team.every(
    (p) => p.level >= 100 && getEvolutionStage(p.slug) === 0 && getEvolutionsFor(p.slug).length > 0,
  )
}

export function useAchievements() {
  const player = usePlayerStore()
  const inventory = useInventoryStore()
  const combat = useCombatStore()

  function buildContext(): AchievementContext {
    const collection = inventory.collection
    const team: OwnedPokemon[] = collection
      .filter((p: OwnedPokemon) => p.teamSlot !== null)
      .sort((a: OwnedPokemon, b: OwnedPokemon) => (a.teamSlot ?? 0) - (b.teamSlot ?? 0))
    const combatGen = player.combatGeneration ?? player.currentGeneration
    const enemyTypes = combat.enemy?.types

    let teamAllZeroDps = false
    if (team.length === 6 && enemyTypes && enemyTypes.length > 0) {
      teamAllZeroDps = team.every(
        (p: OwnedPokemon) =>
          effectiveDpsForAchievement(p, enemyTypes, combatGen, player.isEndgame, player.battleTowerZone) === 0,
      )
    }

    const isHighRarity = (p: OwnedPokemon) =>
      ['mythic', 'legendary', 'uber_mega', 'divine'].includes(p.rarity)
    const teamShinyLegendary5Stars = team.filter(
      (p: OwnedPokemon) => p.isShiny && isHighRarity(p) && p.stars >= MAX_STARS,
    ).length

    return {
      level: player.level,
      badges: player.badges,
      currentGeneration: player.currentGeneration,
      gold: player.gold,
      // totalKills lives on player (DB column), not in stats JSON — expose it for combat achievements
      stats: { ...(player.stats ?? {}), totalKills: player.totalKills },
      collectionCount: collection.length,
      shinyCount: collection.filter((p: OwnedPokemon) => p.isShiny).length,
      legendaryCount: collection.filter((p: OwnedPokemon) => isHighRarity(p)).length,
      shinyLegendaryCount: collection.filter((p: OwnedPokemon) => p.isShiny && isHighRarity(p)).length,
      shinyLegendary5StarCount: collection.filter(
        (p: OwnedPokemon) => p.isShiny && isHighRarity(p) && p.stars >= MAX_STARS,
      ).length,
      teamShinies: team.filter((p: OwnedPokemon) => p.isShiny).length,
      teamLegendaries: team.filter((p: OwnedPokemon) => isHighRarity(p)).length,
      team5Stars: team.filter((p: OwnedPokemon) => p.stars >= MAX_STARS).length,
      teamShinyLegendary5Stars,
      teamAllZeroDps,
      showcase: player.showcase ?? [],
      devLinkClicked: (player.stats?.devLinkClicked ?? 0) >= 1,
      completedPokedexGens: player.completedPokedexGens ?? [],
      unlockedVisibleCount: countUnlockedVisibleAchievements(player.achievements),
      teamTypesNoDuplicate: teamTypesAllUnique(team),
      teamAllBaseFireStarters: teamSixDistinctFromStarterSet(team, FIRE_STARTER_SLUGS),
      teamAllBaseGrassStarters: teamSixDistinctFromStarterSet(team, GRASS_STARTER_SLUGS),
      teamAllBaseWaterStarters: teamSixDistinctFromStarterSet(team, WATER_STARTER_SLUGS),
      teamAshFirst: teamAshFirst(team),
      teamRocketArsenal: teamRocketArsenal(team),
      ownsShinyBidoofLevel100: collection.some(
        (p: OwnedPokemon) => p.slug === 'bidoof' && p.isShiny && p.level >= 100,
      ),
      ownsFullShinyPokedex: ownsFullShinyPokedex(collection),
      teamSharedCommonType: teamSharesCommonType(team),
      teamAllBaseForms: teamAllBaseForms(team),
      teamEvolutionRefused: teamEvolutionRefused(team),
      teamAllGhost: teamAllOfType(team, 'ghost'),
      teamAllDragon: teamAllOfType(team, 'dragon'),
      teamKantoFinalStarters: teamHasAllSlugs(team, KANTO_FINAL_STARTERS),
      teamSixEeveelutions: teamSixEeveelutions(team),
      teamKantoBirds: teamHasAllSlugs(team, KANTO_BIRDS),
      teamJohtoBeasts: teamHasAllSlugs(team, JOHTO_BEASTS),
      teamWeatherTrio: teamHasAllSlugs(team, WEATHER_TRIO),
      teamCreationTrio: teamHasAllSlugs(team, CREATION_TRIO),
      teamTaoTrio: teamHasAllSlugs(team, TAO_TRIO),
      teamEonDuo: teamHasAllSlugs(team, EON_DUO),
      teamHasMega: team.some((p: OwnedPokemon) => MEGA_FORM_RE.test(p.slug)),
      teamHasRegionalForm: team.some((p: OwnedPokemon) => REGIONAL_FORM_RE.test(p.slug)),
      ownsShinyLegendaryLevel100: collection.some(
        (p: OwnedPokemon) => p.isShiny && isHighRarity(p) && p.level >= 100,
      ),
      teamFourArchitects: teamHasAllSlugs(team, FOUR_ARCHITECTS_SLUGS),
    }
  }

  function runCheck(skipThrottle: boolean): AchievementDef[] {
    if (!skipThrottle) {
      const now = Date.now()
      if (now - lastCheckTime < CHECK_INTERVAL_MS) return []
      lastCheckTime = now
    }

    if (!player.isLoggedIn) return []

    const rawAchievements = player.achievements ?? []
    const filteredAchievements = rawAchievements.filter((id) => VALID_ACHIEVEMENT_IDS.has(id))
    if (filteredAchievements.length !== rawAchievements.length) {
      player.achievements = filteredAchievements
    }
    const unlocked = new Set(filteredAchievements)
    const ctx = buildContext()
    const newlyUnlocked: AchievementDef[] = []

    for (const achievement of ALL_ACHIEVEMENTS) {
      if (unlocked.has(achievement.id)) continue
      ctx.unlockedVisibleCount = countVisibleUnlocked(unlocked)
      try {
        if (achievement.check(ctx)) {
          unlocked.add(achievement.id)
          newlyUnlocked.push(achievement)
        }
      } catch {
        // skip broken checks
      }
    }

    if (newlyUnlocked.length > 0) {
      player.achievements = [...unlocked]
      recentUnlocks.value = [...recentUnlocks.value, ...newlyUnlocked]
      setTimeout(() => {
        recentUnlocks.value = recentUnlocks.value.filter(
          (a: AchievementDef) => !newlyUnlocked.includes(a),
        )
      }, 5000)
    }

    return newlyUnlocked
  }

  function checkAchievements(): AchievementDef[] {
    return runCheck(false)
  }

  /** Bypass 10s throttle (guide link, showcase, etc.). */
  function checkAchievementsImmediate(): AchievementDef[] {
    return runCheck(true)
  }

  function dismissUnlock(id: string) {
    recentUnlocks.value = recentUnlocks.value.filter((a: AchievementDef) => a.id !== id)
  }

  return {
    checkAchievements,
    checkAchievementsImmediate,
    recentUnlocks,
    dismissUnlock,
    buildContext,
  }
}
